import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Tv, Film, Mic, Check, ArrowRight, Loader2, User, Globe, Heart, BookOpen } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortaLogo = ({ size = "default" }) => {
  const sizeClasses = { small: "h-8", default: "h-10", large: "h-14" };
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta"
      className={`${sizeClasses[size]} object-contain rounded-lg`}
    />
  );
};

// Country list for selection
const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bangladesh", "Belgium", "Brazil", "Canada",
  "Chile", "China", "Colombia", "Czech Republic", "Denmark", "Egypt", "Finland", "France", "Germany", "Greece",
  "Hong Kong", "Hungary", "India", "Indonesia", "Ireland", "Israel", "Italy", "Japan", "Kenya", "Malaysia",
  "Mexico", "Netherlands", "New Zealand", "Nigeria", "Norway", "Pakistan", "Peru", "Philippines", "Poland", "Portugal",
  "Romania", "Russia", "Saudi Arabia", "Singapore", "South Africa", "South Korea", "Spain", "Sweden", "Switzerland", "Taiwan",
  "Thailand", "Turkey", "UAE", "UK", "Ukraine", "USA", "Vietnam"
];

export default function ProfileSetup({ user }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [avatars, setAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(true);
  
  // Form state
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [username, setUsername] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  
  // Validation errors
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchAvatars();
  }, []);

  const fetchAvatars = async () => {
    try {
      const response = await axios.get(`${API}/profile/avatars`, { withCredentials: true });
      setAvatars(response.data.avatars || []);
    } catch {
      // eslint-disable-next-line no-empty -- Intentional: Avatar loading failure is non-critical, default empty array is acceptable fallback
    } finally {
      setLoadingAvatars(false);
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 2) {
      if (!username.trim()) {
        newErrors.username = 'Username is required';
      } else if (username.trim().length < 2) {
        newErrors.username = 'Username must be at least 2 characters';
      }
    }
    if (currentStep === 3 && selectedInterests.length === 0) {
      newErrors.interests = 'Please select at least one interest';
    }
    if (currentStep === 4 && !selectedCountry) {
      newErrors.country = 'Please select your country';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const toggleInterest = (interest) => {
    if (selectedInterests.includes(interest)) {
      setSelectedInterests(selectedInterests.filter(i => i !== interest));
    } else {
      setSelectedInterests([...selectedInterests, interest]);
    }
    setErrors({ ...errors, interests: null });
  };

  const handleComplete = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    try {
      await axios.put(`${API}/profile`, {
        display_name: username.trim(),
        avatar_url: selectedAvatar,
        interests: selectedInterests,
        country: selectedCountry,
        profile_complete: true
      }, { withCredentials: true });
      
      toast.success('Welcome to Sorta Media!');
      navigate('/tv-shows', { replace: true });
    } catch (error) {
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const interestOptions = [
    { id: 'tv', label: 'TV Shows', icon: Tv, color: 'text-blue-500' },
    { id: 'movies', label: 'Movies', icon: Film, color: 'text-amber-500' },
    { id: 'podcasts', label: 'Podcasts', icon: Mic, color: 'text-purple-500' },
    { id: 'books', label: 'Books', icon: BookOpen, color: 'text-emerald-500' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F87171] via-[#EF4444] to-[#DC2626] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <SortaLogo size="large" />
            <span className="font-outfit text-3xl font-bold text-white">Media</span>
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Create Your Profile</h1>
          <p className="text-white/80">Step {step} of 4</p>
          
          {/* Progress bar */}
          <div className="flex gap-2 mt-4 justify-center">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 w-12 rounded-full transition-colors ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm">
          <CardContent className="p-6">
            {/* Step 1: Avatar Selection (Optional) */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center">
                  <User className="w-12 h-12 mx-auto text-[#F87171] mb-3" />
                  <h2 className="text-xl font-bold text-[#3D4F6F]">Choose Your Avatar</h2>
                  <p className="text-[#64748B] text-sm mt-1">Pick an avatar that represents you (optional)</p>
                </div>
                
                {loadingAvatars ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-[#F87171]" />
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-3">
                    {/* No avatar option */}
                    <button
                      onClick={() => setSelectedAvatar(null)}
                      className={`relative aspect-square rounded-xl border-2 transition-all flex items-center justify-center bg-slate-100 ${
                        selectedAvatar === null ? 'border-[#F87171] ring-2 ring-[#F87171]/30' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <User className="w-8 h-8 text-slate-400" />
                      {selectedAvatar === null && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#F87171] rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </button>
                    
                    {avatars.map((avatar) => (
                      <button
                        key={avatar.id}
                        onClick={() => setSelectedAvatar(avatar.url)}
                        className={`relative aspect-square rounded-xl border-2 transition-all overflow-hidden ${
                          selectedAvatar === avatar.url ? 'border-[#F87171] ring-2 ring-[#F87171]/30' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <img src={avatar.url} alt={avatar.name} className="w-full h-full object-cover" />
                        {selectedAvatar === avatar.url && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#F87171] rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                <Button
                  onClick={handleNext}
                  className="w-full bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full h-12"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Username */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-[#F87171]/10 flex items-center justify-center">
                    {selectedAvatar ? (
                      <Avatar className="w-14 h-14">
                        <AvatarImage src={selectedAvatar} />
                      </Avatar>
                    ) : (
                      <User className="w-8 h-8 text-[#F87171]" />
                    )}
                  </div>
                  <h2 className="text-xl font-bold text-[#3D4F6F]">What should we call you?</h2>
                  <p className="text-[#64748B] text-sm mt-1">This will be your display name</p>
                </div>
                
                <div>
                  <Input
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setErrors({ ...errors, username: null });
                    }}
                    placeholder="Enter your username"
                    className={`h-12 text-center text-lg ${errors.username ? 'border-red-500' : ''}`}
                    maxLength={30}
                    data-testid="username-input"
                  />
                  {errors.username && (
                    <p className="text-red-500 text-sm mt-1 text-center">{errors.username}</p>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 rounded-full h-12"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full h-12"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Interests */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <Heart className="w-12 h-12 mx-auto text-[#F87171] mb-3" />
                  <h2 className="text-xl font-bold text-[#3D4F6F]">What do you love?</h2>
                  <p className="text-[#64748B] text-sm mt-1">Select at least one interest</p>
                </div>
                
                <div className="space-y-3">
                  {interestOptions.map((interest) => {
                    const Icon = interest.icon;
                    const isSelected = selectedInterests.includes(interest.id);
                    return (
                      <button
                        key={interest.id}
                        onClick={() => toggleInterest(interest.id)}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                          isSelected 
                            ? 'border-[#F87171] bg-[#F87171]/5' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`interest-${interest.id}`}
                      >
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-[#F87171]/10' : 'bg-slate-100'
                        }`}>
                          <Icon className={`w-6 h-6 ${isSelected ? 'text-[#F87171]' : interest.color}`} />
                        </div>
                        <span className={`font-medium flex-1 text-left ${isSelected ? 'text-[#F87171]' : 'text-[#3D4F6F]'}`}>
                          {interest.label}
                        </span>
                        {isSelected && (
                          <div className="w-6 h-6 bg-[#F87171] rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                {errors.interests && (
                  <p className="text-red-500 text-sm text-center">{errors.interests}</p>
                )}

                <div className="flex gap-3">
                  <Button
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 rounded-full h-12"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full h-12"
                  >
                    Continue <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Country */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center">
                  <Globe className="w-12 h-12 mx-auto text-[#F87171] mb-3" />
                  <h2 className="text-xl font-bold text-[#3D4F6F]">Where are you from?</h2>
                  <p className="text-[#64748B] text-sm mt-1">This helps personalize your experience</p>
                </div>
                
                <div>
                  <Select value={selectedCountry} onValueChange={(value) => {
                    setSelectedCountry(value);
                    setErrors({ ...errors, country: null });
                  }}>
                    <SelectTrigger className={`h-12 ${errors.country ? 'border-red-500' : ''}`} data-testid="country-select">
                      <SelectValue placeholder="Select your country" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.country && (
                    <p className="text-red-500 text-sm mt-1 text-center">{errors.country}</p>
                  )}
                </div>

                {/* Summary */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-medium text-[#3D4F6F]">Your Profile Summary</p>
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10 border-2 border-[#F87171]">
                      {selectedAvatar ? (
                        <AvatarImage src={selectedAvatar} />
                      ) : (
                        <AvatarFallback className="bg-[#F87171] text-white">
                          {username.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div>
                      <p className="font-medium text-[#3D4F6F]">{username}</p>
                      <p className="text-xs text-[#64748B]">
                        {selectedInterests.map(i => i.charAt(0).toUpperCase() + i.slice(1)).join(', ')}
                        {selectedCountry && ` • ${selectedCountry}`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleBack}
                    variant="outline"
                    className="flex-1 rounded-full h-12"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleComplete}
                    disabled={loading}
                    className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full h-12"
                    data-testid="complete-profile-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>Complete Setup</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
